/**
 * Ryan O'Dowd
 * 2024-09-22
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Platform,
  View,
} from 'react-native';
import ListItem from '@components/ListItem';
import React from 'react';
import Text from '@components/common/Text';
import WalkthroughTooltip from '@components/WalkthroughTooltip';
import WalkthroughTooltipContent from '@components/WalkthroughTooltip/Content';
import getStyles from './styles';
import hooks from '@hooks';

const Container = (props: ContainerProps) => {
  const styles = hooks.useStyles(getStyles);

  const width = props.cellSize * props.layout.w;
  const height = props.cellSize * props.layout.h;

  return (
    <View
      style={[
        styles.container,
        {
          width,
          height,
          top: props.layout.y * props.cellSize,
          left: props.layout.x * props.cellSize,
        },
      ]}
    >
      <WalkthroughTooltip
        placement={props.walkthroughProps?.placement}
        isVisible={props.walkthroughProps?.isVisible}
        onClose={props.walkthroughProps?.onClose}
        closeOnChildInteraction={true}
        closeOnContentInteraction={false}
        allowChildInteraction={true}
        topAdjustment={Platform.OS === 'android' ? -styles.safeAreaInsets.top : 0}// @TODO: from docs: Value which provides additional vertical offest for the child element displayed in a tooltip. Commonly set to: Platform.OS === 'android' ? -StatusBar.currentHeight : 0 due to an issue with React Native's measure function on Android (native only obviously)
        content={(
          props.walkthroughProps?.title
            ? (
              <WalkthroughTooltipContent
                title={props.walkthroughProps?.title}
                content={(
                  <View style={styles.walkthroughContentRow}>
                    <Text style={styles.walkthroughText}>{props.walkthroughProps?.body}</Text>
                  </View>
                )}
              />
            )
            : null
        )}
      >
        <ListItem
          containerStyles={styles.listItemContainer}
          innerStyles={[
            styles.listItemInner,
            {
              width: props.layout.x !== 0 ? width - 8 : width - 4,
              height: height - 4,
            },
          ]} // @TODO: padding/margin is off (compare 2-column vs. 3-column layouts...padding on right is different than left in 2-column) // @TODO: too much hardcoded here
          variant={props.complete ? 'accent' : 'primary'}
        >
          {props.children}
        </ListItem>
      </WalkthroughTooltip>
    </View>
  );
};

class ContainerProps {
  children: React.ReactNode;
  layout: {
    x: number,
    y: number,
    w: number,
    h: number,
  }; // @TODO: type
  cellSize: number;

  complete?: boolean;
  walkthroughProps?: {
    isVisible: boolean;
    onClose: () => void;
    title: string;
    body: string;
    placement: 'left' | 'right' | 'top' | 'bottom' | 'center';
  }; // @TODO: type
};

export default Container;
